import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis, Legend } from 'recharts';
import moment from 'moment';
import BugReportIcon from '@mui/icons-material/BugReport';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { rangeFromQuery } from '../../components/usage/usage-period-filter';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import { UsageEllipsisCell, UsageKpiCard, UsageApiChip, UsageStatusChip, UsageEmpty, UsageBlockTitle, UsageOriginChip, SELECTED_ROW_CLASS, fmtDateTime, fmtInt, fmtMs } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Erros: operação, não segurança (recusas ficam na aba Recusas). Série diária do rollup (até 400
// dias) + rotas com erro e ocorrências do fato (até 90 dias), com a exceção ligada pelo
// TraceIdentifier. Anônimos entram: erro sem usuário importa tanto quanto com.
// Falhas de login ficam fora (já contadas em Recusas). Sem async em arrow class field.
// Filtro de família (kind: all | 5xx | 4xx) é UM estado: os cards de KPI (clique liga, novo
// clique desliga), o grupo "Mostrar" e o chip "Filtrando" do gráfico apontam para ele.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const MAX_FACT_DAYS = 90;
const COLOR_5XX = '#D32F2F';
const COLOR_4XX = '#F57C00';

export default class UsageErrors extends Component {
  constructor(props) {
    super(props);
    const q0 = new URLSearchParams(props.location?.search || '');
    this.state = {
      filter: { ...rangeFromQuery(q0, '7d'), api: '', actionGroup: '', userId: 0, userName: '', ip: props.ip || '' },
      kind: 'all',
      loading: false, loaded: false,
      totals: { server5xx: 0, client4xx: 0, routes5xx: 0, routes4xx: 0 }, routes: [], byDay: [],
      selected: null, occurrences: [], occCount: 0, occPage: 1, occLoading: false,
      drawerRow: null, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.setKind = this.setKind.bind(this);
    this.toggleKind = this.toggleKind.bind(this);
    this.selectRoute = this.selectRoute.bind(this);
    this.onOccPage = this.onOccPage.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(); }

  // Montada permanentemente (index.js). Querystring nova com a aba ativa → aplica período e recarrega.
  componentDidUpdate(prevProps) {
    // IP vem da área (index.js): um valor para as 4 abas do fato. Mudou em OUTRA aba → aplica aqui e
    // recarrega o que já estava carregado. Mudou NESTA aba, o filtro já está igual (onFilter) e nada acontece.
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }), () => { if (this.state.loaded) this.load(1); });
      return;
    }
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      const q = new URLSearchParams(search);
      if (q.get('start') && q.get('end')) {
        this.setState((s) => ({ filter: { ...s.filter, preset: 'custom', dateStart: moment(q.get('start')).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(q.get('end')).endOf('day').format('YYYY/MM/DD HH:mm:ss') } }), this.load);
      }
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) {
    // IP é da área: avisa o index.js (que repassa às outras abas) e segue com o filtro local.
    if ((next.ip || '') !== (this.state.filter.ip || '') && this.props.onIpChange) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  // kind é filtro do SERVIDOR para rotas/ocorrências (faixa de status) e do CLIENTE para o gráfico
  // (a série diária já vem com as duas famílias). Card: clique liga, clique no ativo volta a 'all'.
  setKind(kind) { if (kind && kind !== this.state.kind) this.setState({ kind, selected: null, occurrences: [], occCount: 0 }, this.load); }
  toggleKind(kind) { this.setKind(this.state.kind === kind ? 'all' : kind); }
  tooLong() { const f = this.state.filter; return moment(f.dateEnd).diff(moment(f.dateStart), 'days') + 1 > MAX_FACT_DAYS; }

  load() {
    const f = this.state.filter;
    this.setState({ loading: true, selected: null, occurrences: [], occCount: 0 });
    Promise.all([
      this.tooLong() ? Promise.resolve({ totals: { server5xx: 0, client4xx: 0, routes5xx: 0, routes4xx: 0 }, routes: [] }) : UsageService.errorsSummary(f, this.state.kind, 100),
      f.ip ? Promise.resolve([]) : UsageService.errorsByDay(f), // o daily não guarda IP
    ]).then(([summary, byDay]) => this.setState({ loading: false, loaded: true, totals: summary.totals, routes: summary.routes, byDay }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false, snack: { open: true, variant: 'error', message: err.message } }); });
  }

  selectRoute(r) {
    if (this.state.selected && this.state.selected.route === r.route && this.state.selected.statusCode === r.statusCode) { this.setState({ selected: null, occurrences: [], occCount: 0 }); return; }
    this.setState({ selected: r }, () => this.loadOccurrences(1));
  }

  loadOccurrences(page) {
    const { filter, selected } = this.state;
    if (!selected) return;
    this.setState({ occLoading: true, occPage: page });
    UsageService.errorOccurrences(filter, selected.route, selected.statusCode, page, PAGE_SIZE)
      .then((res) => this.setState({ occLoading: false, occurrences: res.data, occCount: res.count }))
      .catch((err) => { Api.kickoff(err); this.setState({ occLoading: false, snack: { open: true, variant: 'error', message: err.message } }); });
  }

  onOccPage(e, page) { this.loadOccurrences(page); }

  renderByDay() {
    const { intl } = this.props;
    const { kind } = this.state;
    const data = this.state.byDay.map((d) => ({ label: moment(d.day).format('DD/MM'), errors: d.errors, clientErrors: d.clientErrors }));
    if (!data.length) return <UsageEmpty intl={intl} compact />;
    return (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis allowDecimals={false} /><ChartTooltip formatter={(v, n) => [fmtInt(v), n]} /><Legend />
          {kind !== '4xx' && <Bar dataKey="errors" name={this.tr('usage.errors.series.5xx', 'Erros 5xx')} stackId="e" fill={COLOR_5XX} />}
          {kind !== '5xx' && <Bar dataKey="clientErrors" name={this.tr('usage.errors.series.4xx', 'Erros 4xx')} stackId="e" fill={COLOR_4XX} />}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  renderRoutes() {
    const { intl } = this.props;
    const { routes, selected, kind } = this.state;
    const anon = this.tr('usage.anonymous', 'anônimo');
    if (this.tooLong()) return <UsageEmpty intl={intl} compact title={this.tr('usage.errors.tooLong', 'Período acima de 90 dias: reduza o período para listar as rotas (leitura do histórico detalhado).')} />;
    if (!routes.length) return <UsageEmpty intl={intl} compact title={kind === '5xx' ? this.tr('usage.errors.none5xx', 'Nenhum erro 5xx no período') : kind === '4xx' ? this.tr('usage.errors.none4xx', 'Nenhum erro 4xx no período') : this.tr('usage.errors.empty.title', 'Nenhum erro no período')} />;
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>API</TableCell><TableCell>{this.tr('usage.col.route', 'Rota')}</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">{this.tr('usage.errors.col.count', 'Ocorrências')}</TableCell>
            <TableCell>{this.tr('usage.errors.col.first', 'Primeira')}</TableCell><TableCell>{this.tr('usage.errors.col.last', 'Última')}</TableCell>
            <TableCell align="right">{this.tr('usage.errors.col.users', 'Usuários')}</TableCell><TableCell align="right">IPs</TableCell>
            <TableCell>{this.tr('usage.errors.col.exceptionOrReason', 'Última exceção / motivo')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {routes.map((r) => {
              const isSel = selected && selected.route === r.route && selected.statusCode === r.statusCode;
              return (
                <TableRow key={`${r.api}|${r.route}|${r.statusCode}`} hover selected={!!isSel} className={isSel ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer' }} onClick={() => this.selectRoute(r)}>
                  <TableCell><UsageApiChip api={r.api} /></TableCell>
                  <TableCell><UsageEllipsisCell text={r.route} maxWidth={320} mono /></TableCell>
                  <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtInt(r.count)}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.firstAt)}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.lastAt)}</TableCell>
                  <TableCell align="right">{fmtInt(r.users)}{r.anonymous > 0 ? <Typography variant="caption" style={{ color: '#888' }}> · {fmtInt(r.anonymous)} {anon}</Typography> : null}</TableCell>
                  <TableCell align="right">{fmtInt(r.ips)}</TableCell>
                  <TableCell style={{ maxWidth: 360 }}>
                    {r.lastExceptionType ? (
                      <Tooltip title={r.lastExceptionMessage || ''}><Box>
                        <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }} noWrap>{r.lastExceptionType}</Typography>
                        <Typography variant="caption" style={{ color: '#666', display: 'block' }} noWrap>{r.lastExceptionMessage}</Typography>
                      </Box></Tooltip>
                    ) : r.lastReason ? (
                      <Tooltip title={r.lastReason}><Typography variant="caption" style={{ color: '#666', display: 'block' }} noWrap>{r.lastReason}</Typography></Tooltip>
                    ) : <Typography variant="caption" style={{ color: '#bbb' }}>{this.tr('usage.errors.noException', 'sem exceção registrada')}</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  renderOccurrences() {
    const { intl } = this.props;
    const { selected, occurrences, occCount, occPage, occLoading } = this.state;
    if (!selected) return null;
    const anon = this.tr('usage.anonymous', 'anônimo');
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: `4px solid ${selected.statusCode >= 500 ? COLOR_5XX : COLOR_4XX}` }}><CardContent style={{ paddingBottom: 8 }}>
        <UsageBlockTitle title={<span>{this.tr('usage.errors.occ.title', 'Ocorrências')} · <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{selected.route}</span> · <UsageStatusChip code={selected.statusCode} /></span>}
          subtitle={`${fmtInt(occCount)} ${this.tr('usage.errors.occ.subtitle', 'no período · clique na linha para ver o request e a exceção')}`}
          right={<Button size="small" onClick={() => this.setState({ selected: null, occurrences: [], occCount: 0 })}>{this.tr('usage.errors.occ.close', 'fechar')}</Button>} />
        {occLoading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        <Box style={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell><TableCell>IP</TableCell>
              <TableCell>{this.tr('usage.col.origin', 'Origem')}</TableCell><TableCell align="right">{this.tr('usage.col.duration', 'Duração')}</TableCell>
              <TableCell>{this.tr('usage.errors.col.exceptionOrReason', 'Exceção / motivo')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {occurrences.map((r) => (
                <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => this.setState({ drawerRow: r })}>
                  <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt)}</TableCell>
                  <TableCell><UsageEllipsisCell text={r.userName || (r.userId ? `#${r.userId}` : anon)} maxWidth={200} /></TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}</TableCell>
                  <TableCell><UsageOriginChip intl={intl} row={r} maxWidth={180} /></TableCell>
                  <TableCell align="right">{fmtMs(r.durationMs)}</TableCell>
                  <TableCell style={{ maxWidth: 420 }}>
                    {r.exceptionType ? (
                      <Tooltip title={r.exceptionMessage || ''}><Box>
                        <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }} noWrap>{r.exceptionType}</Typography>
                        <Typography variant="caption" style={{ color: '#666', display: 'block' }} noWrap>{r.exceptionMessage}</Typography>
                      </Box></Tooltip>
                    ) : r.details && r.details.errors ? (
                      <Tooltip title={r.details.errors}><Typography variant="caption" style={{ color: '#666', display: 'block' }} noWrap>{r.details.errors}</Typography></Tooltip>
                    ) : <Typography variant="caption" style={{ color: '#bbb' }}>-</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {occCount > PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination count={Math.ceil(occCount / PAGE_SIZE)} page={occPage} onChange={this.onOccPage} /></Box>}
      </CardContent></Card>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, loading, loaded, totals, kind, drawerRow, snack } = this.state;
    const empty = loaded && totals.server5xx === 0 && totals.client4xx === 0 && !this.tooLong();
    const filterTip = this.tr('usage.errors.kpi.filterTip', 'Clique para filtrar o gráfico e a lista; clique de novo para limpar');
    const kindColor = kind === '5xx' ? COLOR_5XX : COLOR_4XX;
    const filteringChip = kind !== 'all' ? <Chip size="small" label={`${this.tr('usage.errors.filtering', 'Filtrando')}: ${kind}`} onDelete={() => this.setKind('all')} style={{ backgroundColor: kindColor, color: '#fff' }} /> : null;
    return (
      <Box>
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showGroup={false} showUser={false} onApply={this.load} applying={loading} />
        {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
        {loaded && (
          <Box style={{ marginTop: 16 }}>
            {filter.ip && <Alert severity="info" style={{ marginBottom: 12 }}>{this.tr('usage.ip.noRollup', 'Filtro por IP ativo: totais e gráficos por dia vêm do consolidado diário, que não guarda IP, e ficam ocultos. Abaixo, só o histórico detalhado desse IP (período de até 90 dias).')}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><Tooltip title={filterTip}><Box style={{ height: '100%' }}><UsageKpiCard intl={intl} title={this.tr('usage.errors.kpi.5xx', 'Erros 5xx')} value={totals.server5xx} color={COLOR_5XX} onClick={() => this.toggleKind('5xx')} active={kind === '5xx'} /></Box></Tooltip></Grid>
              <Grid item xs={6} md={3}><Tooltip title={filterTip}><Box style={{ height: '100%' }}><UsageKpiCard intl={intl} title={this.tr('usage.errors.kpi.4xx', 'Erros 4xx')} value={totals.client4xx} color={COLOR_4XX} subtitle={this.tr('usage.errors.kpi.4xxHint', 'sem falhas de login')} onClick={() => this.toggleKind('4xx')} active={kind === '4xx'} /></Box></Tooltip></Grid>
              <Grid item xs={6} md={3}><Tooltip title={filterTip}><Box style={{ height: '100%' }}><UsageKpiCard intl={intl} title={this.tr('usage.errors.kpi.routes5xx', 'Rotas com 5xx')} value={totals.routes5xx} color={COLOR_5XX} onClick={() => this.toggleKind('5xx')} active={kind === '5xx'} /></Box></Tooltip></Grid>
              <Grid item xs={6} md={3}><Tooltip title={filterTip}><Box style={{ height: '100%' }}><UsageKpiCard intl={intl} title={this.tr('usage.errors.kpi.routes4xx', 'Rotas com 4xx')} value={totals.routes4xx} color={COLOR_4XX} onClick={() => this.toggleKind('4xx')} active={kind === '4xx'} /></Box></Tooltip></Grid>
            </Grid>
            {empty ? <Box style={{ marginTop: 16 }}><UsageEmpty intl={intl} icon={<BugReportIcon style={{ fontSize: 56 }} />} title={this.tr('usage.errors.empty.title', 'Nenhum erro no período')} description={this.tr('usage.errors.empty.description', 'Respostas 5xx e 4xx das APIs aparecem aqui, com a exceção ligada ao request.')} /></Box> : (
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                {!filter.ip && (
                <Grid item xs={12}>
                  <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}><CardContent>
                    <UsageBlockTitle title={this.tr('usage.errors.byDay.title', 'Por dia')} subtitle={this.tr('usage.errors.byDay.subtitle', '5xx e 4xx empilhados · consolidado')} right={filteringChip} />
                    {this.renderByDay()}
                  </CardContent></Card>
                </Grid>
                )}
                <Grid item xs={12}>
                  <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}><CardContent style={{ paddingBottom: 8 }}>
                    <UsageBlockTitle title={this.tr('usage.errors.routes.title', 'Rotas com erro')} subtitle={this.tr('usage.errors.routes.subtitle', 'Agrupadas por rota e status · clique para ver as ocorrências · até 90 dias')} right={
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.errors.show', 'Mostrar')}:</Typography>
                        <ToggleButtonGroup size="small" exclusive value={kind} onChange={(e, v) => this.setKind(v)}>
                          <ToggleButton value="all" style={{ padding: '2px 10px', textTransform: 'none' }}>{this.tr('usage.errors.show.all', 'Todos')}</ToggleButton>
                          <ToggleButton value="5xx" style={{ padding: '2px 10px', textTransform: 'none' }} sx={{ '&.Mui-selected': { backgroundColor: COLOR_5XX, color: '#fff' }, '&.Mui-selected:hover': { backgroundColor: COLOR_5XX } }}>5xx</ToggleButton>
                          <ToggleButton value="4xx" style={{ padding: '2px 10px', textTransform: 'none' }} sx={{ '&.Mui-selected': { backgroundColor: COLOR_4XX, color: '#fff' }, '&.Mui-selected:hover': { backgroundColor: COLOR_4XX } }}>4xx</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>} />
                    {this.renderRoutes()}
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12}>{this.renderOccurrences()}</Grid>
              </Grid>
            )}
          </Box>
        )}
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={() => this.setState({ drawerRow: null })} lang={this.props.lang} history={this.props.history} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
